import { twJoin } from '@beerntea/pitcher-ui';
import { MenuItem } from './MenuItem';
import { useEffect, useState } from 'react';
import { PlusButton } from '@components/PlusButton';
import { UnderlineHoverText } from '@components/UnderlineHoverText';
import { XIcon } from 'lucide-react';
import { useRef } from 'react';
import gsap from 'gsap';
import { useGSAP } from '@gsap/react';
import { useMenuContext } from '@/contexts/MenuContext.tsx';
import { MenuItemSmall } from '@components/Menu/MenuItemSmall.tsx';
import { createPortal } from 'react-dom';

gsap.registerPlugin(useGSAP);
export type MenuItem = {
  label: string;
  to: string;
  bg: string;
  bg_video?: string;
  isVideo?: boolean;
};
export type SubMenuItem = {
  url: string;
  label: string;
};
export type SocialItem = {
  icon: JSX.Element;
  url: string;
};
interface Props {
  navItems: MenuItem[];
  subMenu: SubMenuItem[];
  socials: SocialItem[];
}
export function Menu({ subMenu, navItems, socials }: Props) {
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const { menuOpen, setMenuOpen, invert } = useMenuContext();

  const handleMouseEnter = (index: number) => {
    setActiveIndex(index);
  };

  const handleMouseLeave = () => {
    setActiveIndex(null);
  };

  const containerRef = useRef<HTMLDivElement>(null);
  const btnClose = useRef<HTMLButtonElement>(null);
  const btnMenu = useRef<HTMLButtonElement>(null);
  const bgRefs = useRef<(HTMLDivElement | null)[]>([]);
  const navRefs = useRef<(HTMLLIElement | null)[]>([]);
  const subNavRef = useRef<HTMLDivElement>(null);

  useGSAP(() => {
    bgRefs.current.forEach((el, index) => {
      if (!el) return;
      if (index === activeIndex) {
        gsap.to(el, { opacity: 1, duration: 0.5, ease: 'power2.out' });
      } else {
        gsap.to(el, { opacity: 0, duration: 0.5, ease: 'power2.out' });
      }
    });
  }, [activeIndex]);

  useGSAP(() => {
    if (menuOpen) {
      gsap.fromTo(
        navRefs.current.filter(Boolean),
        { x: 100, opacity: 0 },
        { x: 0, opacity: 1, duration: 0.6, stagger: 0.1, ease: 'power3.out' },
      );
      gsap.to(btnClose.current, { rotate: 180 });
      gsap.to(btnMenu.current, { opacity: 0 });
    } else {
      if (btnClose.current) gsap.to(btnClose.current, { rotate: -180 });
      if (btnMenu.current) gsap.to(btnMenu.current, { opacity: 1 });
    }
  }, [menuOpen]);

  const handleClose = () => {
    const refs = navRefs.current.filter(Boolean);
    gsap.to(refs, {
      x: 100,
      opacity: 0,
      duration: 0.4,
      stagger: 0.05,
      ease: 'power3.in',
      onComplete: () => {
        setMenuOpen(false);
      },
    });

    gsap.to(subNavRef.current, {
      y: 20,
      opacity: 0,
      duration: 0.4,
      ease: 'power3.in',
    });

    gsap.to(containerRef.current, {
      opacity: 0,
      duration: 0.4,
      ease: 'power3.in',
      delay: 0.2,
    });
  };

  useGSAP(() => {
    if (subNavRef.current) {
      gsap.fromTo(
        subNavRef.current,
        {
          y: 20,
          opacity: 0,
        },
        {
          y: 0,
          opacity: 1,
          duration: 0.6,
          ease: 'power2.out',
        },
      );
    }
  }, [menuOpen]);

  useEffect(() => {
    if (menuOpen) {
      const scrollBarComp = window.innerWidth - document.documentElement.clientWidth;
      document.body.style.overflow = 'hidden';
      document.body.style.paddingRight = `${scrollBarComp}px`;
      return;
    }

    document.body.style.overflow = '';
    document.body.style.paddingRight = '';
  }, [menuOpen]);

  return (
    <>
      <button
        ref={btnMenu}
        type="button"
        className={twJoin('tw-cursor-pointer tw-pointer-events-auto tw-group tw-z-menu-open-button')}
        onClick={() => setMenuOpen(true)}
      >
        <UnderlineHoverText
          textClassName="tw-text-white tw-font-display tw-font-light tw-leading-4 tw-text-shadow tw-tracking-tight"
          lineClassName="tw-bg-white tw-h-[2px]"
        >
          MENU
        </UnderlineHoverText>
      </button>
      {menuOpen &&
        createPortal(
          <div
            ref={containerRef}
            className="tw-fixed tw-h tw-w-full tw-inset-0 tw-overflow-clip tw-z-menu"
            onClick={handleClose}
          >
            <div
              className={twJoin(
                'tw-fixed tw-w-full md:tw-w-auto tw-h-auto md:tw-h-auto tw-right-0 tw-ml-auto tw-overflow-y-auto tw-bg-black tw-z-menu ',
                invert ? 'tw-invert' : '',
              )}
            >
              <div className="tw-h-20  tw-flex tw-justify-end tw-fixed tw-z-[99] tw-right-0">
                <button
                  ref={btnClose}
                  type="button"
                  className="tw-font-display tw-font-light tw-cursor-pointer tw-text-white tw-z-menu-close-button tw-px-8 tw-py-4 sm:tw-px-20 tw-right-0"
                  onClick={handleClose}
                >
                  <XIcon />
                </button>
              </div>
              <div className={twJoin('tw-relative tw-inset-0 tw-px-8 sm:tw-px-8 tw-pt-20 sm:tw-py-20 ')}>
                <nav>
                  <ul>
                    {navItems.map((item, index) => (
                      <li
                        key={item.label}
                        ref={(el) => (navRefs.current[index] = el)}
                      >
                        <MenuItemSmall
                          to={item.to}
                          counter={index + 1}
                          onMouseEnter={() => handleMouseEnter(index)}
                          onMouseLeave={handleMouseLeave}
                          onClick={handleClose}
                        >
                          {item.label}
                        </MenuItemSmall>
                      </li>
                    ))}
                  </ul>
                </nav>
                <div
                  ref={subNavRef}
                  className="tw-py-8"
                >
                  <div className="tw-px-8 tw-flex tw-items-center tw-gap-4 tw-text-white tw-fill-light-gray tw-max-h-20 tw-py-6 tw-flex-wrap">
                    {socials.map((social) => (
                      <a
                        key={social.url}
                        href={social.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={twJoin('tw-opacity-80 tw-transition', 'hover:tw-opacity-100')}
                      >
                        {social.icon}
                      </a>
                    ))}
                  </div>

                  <div className="max-sm:tw-py-8 tw-flex tw-flex-wrap md:tw-flex-col tw-mx-auto md:tw-flex-1 tw-items-start tw-flex-row">
                    {subMenu.map(({ url, label }) => (
                      <PlusButton
                        key={url}
                        url={url}
                        className="tw-text-left"
                      >
                        {label}
                      </PlusButton>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>,
          document.body,
        )}
    </>
  );
}
